import * as z from "zod";

const appliedWritesSchema = z.object({
  appliedWrites: z.array(z.object({ name: z.string().min(1) })).min(1),
});

/**
 * Names of the data changes a finished tool call made, for pages that reload
 * when one of them lands: the tool name for a mutating tool, the applied RPC
 * names for execute_code. Empty when nothing changed.
 */
export function toolDataChanges(
  toolName: string,
  output: unknown,
): readonly string[] {
  const value = parseIfJson(output);
  if (toolName === "execute_code") {
    const parsed = appliedWritesSchema.safeParse(value);
    return parsed.success
      ? parsed.data.appliedWrites.map((write) => write.name)
      : [];
  }
  return isOmsMutationResult(toolName, value) ? [toolName] : [];
}

function parseIfJson(output: unknown): unknown {
  if (typeof output !== "string") {
    return output;
  }
  try {
    return JSON.parse(output);
  } catch {
    return output;
  }
}

function isOmsMutationResult(toolName: string, value: unknown): boolean {
  switch (toolName) {
    case "create-views":
      return z
        .object({
          created: z.array(z.object({ viewId: z.string().min(1) })).min(1),
        })
        .safeParse(value).success;
    case "create-folder":
      return z.object({ folderId: z.string().min(1) }).safeParse(value).success;
    case "update-view":
      return z.object({ viewId: z.string().min(1) }).safeParse(value).success;
    case "create-packing-slip-template":
    case "update-packing-slip-template":
      return z.object({ id: z.string().min(1) }).safeParse(value).success;
    case "upsert-parcel":
      return z
        .object({
          name: z.string().min(1),
          parcelType: z.enum(["carrier", "custom"]),
        })
        .safeParse(value).success;
    case "workstation_tool":
      return z.object({ success: z.literal(true) }).safeParse(value).success;
    case "publish_automation":
      return (
        typeof value === "string" &&
        value.startsWith("Automation ") &&
        (value.includes(" published successfully ") ||
          value.includes(" updated successfully "))
      );
    case "submit_carrier_claim":
      return z.object({ claimId: z.string().min(1) }).safeParse(value).success;
    case "set_automation_enabled":
      return (
        typeof value === "string" &&
        /^Automation (enabled|disabled) \(flowId:/.test(value)
      );
    default:
      return false;
  }
}
